import { motion } from "framer-motion";
import { FileText, Scale, AlertCircle, Shield, Users, Flag } from "lucide-react";

interface TermsProps {
  onNavigate?: (page: string) => void;
}

export function Terms({ onNavigate }: TermsProps) {
  return (
    <div className="min-h-screen pt-32 pb-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/50 border border-primary/20 mb-6">
            <Scale className="w-4 h-4 text-primary" />
            <span className="text-sm text-primary">Terms of Use</span>
          </div>
          <h1 className="text-4xl sm:text-5xl mb-6">
            Terms of Use
          </h1>
          <p className="text-xl text-muted-foreground mb-4">
            Last updated: October 10, 2025
          </p>
        </motion.div>

        {/* Content */}
        <motion.div 
          className="prose prose-lg max-w-none space-y-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <section className="space-y-4">
            <h2 className="text-2xl flex items-center gap-3">
              <FileText className="w-6 h-6 text-primary" />
              Acceptance of Terms
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing or using CannaBias ("the Platform"), you agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use the Platform. We reserve the right to modify these terms at any time, and continued use constitutes acceptance of modified terms.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Eligibility</h2>
            <p className="text-muted-foreground leading-relaxed">
              To use CannaBias, you must:
            </p>
            <ul className="space-y-2 text-muted-foreground ml-6">
              <li>• Be at least 21 years of age (or the legal age in your jurisdiction)</li>
              <li>• Have legal authorization to access cannabis-related information in your location</li>
              <li>• Provide accurate information during registration</li>
              <li>• Comply with all applicable local, state, and federal laws</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Platform Purpose</h2>
            <p className="text-muted-foreground leading-relaxed">
              CannaBias is an <strong>information and review platform</strong> designed to help patients share experiences with medical cannabis products. The Platform:
            </p>
            <ul className="space-y-2 text-muted-foreground ml-6">
              <li>• Does NOT sell, distribute, or facilitate the sale of cannabis products</li>
              <li>• Does NOT provide medical advice, diagnosis, or treatment recommendations</li>
              <li>• Does NOT replace consultation with qualified healthcare providers</li>
              <li>• Provides patient-generated reviews and ratings for informational purposes only</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl flex items-center gap-3">
              <Users className="w-6 h-6 text-primary" />
              User Accounts & Responsibilities
            </h2>
            
            <h3 className="text-xl">Account Creation</h3>
            <ul className="space-y-2 text-muted-foreground ml-6">
              <li>• You may use a pseudonym to protect your privacy</li>
              <li>• You must provide a valid email address for account verification</li>
              <li>• You are responsible for maintaining account security</li>
              <li>• One account per person; no shared or multiple accounts</li>
            </ul>

            <h3 className="text-xl">Prohibited Conduct</h3>
            <p className="text-muted-foreground leading-relaxed">
              You agree NOT to:
            </p>
            <ul className="space-y-2 text-muted-foreground ml-6">
              <li>• Post false, misleading, or fraudulent reviews</li>
              <li>• Provide specific dosage recommendations or medical advice</li>
              <li>• Make unsubstantiated medical claims about cannabis products</li>
              <li>• Harass, threaten, or abuse other users</li>
              <li>• Attempt to buy, sell, or trade cannabis products through the Platform</li>
              <li>• Post personal identifying information of others without consent</li>
              <li>• Use automated systems (bots) to post reviews or collect data</li>
              <li>• Violate any applicable laws or regulations</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Content & Reviews</h2>
            
            <h3 className="text-xl">Your Content</h3>
            <p className="text-muted-foreground leading-relaxed">
              When you post a review or other content, you:
            </p>
            <ul className="space-y-2 text-muted-foreground ml-6">
              <li>• Retain ownership of your content</li>
              <li>• Grant CannaBias a worldwide, non-exclusive license to display, distribute, and use your content on the Platform</li>
              <li>• Represent that your content is accurate and does not violate third-party rights</li>
              <li>• Acknowledge that your content may remain visible even after account deletion (as "Anonymous User")</li>
            </ul>

            <h3 className="text-xl">Content Guidelines</h3>
            <p className="text-muted-foreground leading-relaxed">
              All reviews must:
            </p>
            <ul className="space-y-2 text-muted-foreground ml-6">
              <li>• Be based on personal experience with the reviewed product</li>
              <li>• Focus on quality, consistency, packaging, and availability</li>
              <li>• Avoid specific dosage recommendations</li>
              <li>• Avoid medical claims or treatment suggestions</li>
              <li>• Be respectful and free from profanity or harassment</li>
              <li>• Comply with our{" "}
                <button 
                  onClick={() => onNavigate && onNavigate("moderation")}
                  className="text-primary hover:underline"
                >
                  Moderation Policy
                </button>
              </li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl flex items-center gap-3">
              <Flag className="w-6 h-6 text-primary" />
              Moderation & Removal
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to moderate, edit, or remove any content that:
            </p>
            <ul className="space-y-2 text-muted-foreground ml-6">
              <li>• Violates these Terms of Use</li>
              <li>• Contains prohibited dosage advice or medical claims</li>
              <li>• Is reported by other users as inappropriate</li>
              <li>• We determine, in our sole discretion, to be harmful or objectionable</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed">
              All moderation actions are logged and published quarterly in our{" "}
              <button 
                onClick={() => onNavigate && onNavigate("transparency")}
                className="text-primary hover:underline"
              >
                Transparency Report
              </button>
              .
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-primary" />
              Disclaimers
            </h2>
            
            <div className="p-6 rounded-2xl bg-accent/30 border border-primary/20">
              <h3 className="text-xl mb-4">No Medical Advice</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                CannaBias does NOT provide medical advice, diagnosis, or treatment. All information on the Platform is for informational and educational purposes only. Patient reviews reflect individual experiences and should not be considered medical recommendations.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                <strong>Always consult with a qualified healthcare provider</strong> before starting, stopping, or changing any medical treatment, including cannabis use.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-accent/30 border border-primary/20">
              <h3 className="text-xl mb-4">No Product Sales</h3>
              <p className="text-muted-foreground leading-relaxed">
                CannaBias does NOT sell, distribute, or facilitate the sale of cannabis products. We are a review and information platform only. Any purchases must be made through licensed dispensaries in accordance with local laws.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-accent/30 border border-primary/20">
              <h3 className="text-xl mb-4">No Warranties</h3>
              <p className="text-muted-foreground leading-relaxed">
                The Platform is provided "AS IS" without warranties of any kind, express or implied. We do not guarantee the accuracy, completeness, or reliability of any user-generated content, including reviews and ratings.
              </p>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              To the maximum extent permitted by law, CannaBias and its affiliates shall not be liable for:
            </p>
            <ul className="space-y-2 text-muted-foreground ml-6">
              <li>• Any indirect, incidental, special, or consequential damages</li>
              <li>• Decisions made based on information from the Platform</li>
              <li>• User-generated content or reviews</li>
              <li>• Cannabis products purchased from third-party dispensaries</li>
              <li>• Interruption or termination of Platform access</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Intellectual Property</h2>
            <p className="text-muted-foreground leading-relaxed">
              The CannaBias name, logo, design, and Platform functionality are protected by copyright, trademark, and other intellectual property laws. You may not:
            </p>
            <ul className="space-y-2 text-muted-foreground ml-6">
              <li>• Reproduce, distribute, or create derivative works without permission</li>
              <li>• Use our trademarks or branding without written authorization</li>
              <li>• Scrape, harvest, or collect Platform data using automated means</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl flex items-center gap-3">
              <Shield className="w-6 h-6 text-primary" />
              Privacy & Data Protection
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Your use of the Platform is also governed by our{" "}
              <button 
                onClick={() => onNavigate && onNavigate("privacy")}
                className="text-primary hover:underline"
              >
                Privacy Policy
              </button>
              , which describes how we collect, use, and protect your personal information. We are committed to Cyber Essentials alignment and UK GDPR data protection standards.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Termination</h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to suspend or terminate your account at any time, with or without notice, for:
            </p>
            <ul className="space-y-2 text-muted-foreground ml-6">
              <li>• Violation of these Terms of Use</li>
              <li>• Fraudulent or abusive behavior</li>
              <li>• At our discretion if we believe it's necessary to protect the Platform or other users</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed">
              You may terminate your account at any time through your{" "}
              <button 
                onClick={() => onNavigate && onNavigate("account")}
                className="text-primary hover:underline"
              >
                Account Settings
              </button>
              .
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Indemnification</h2>
            <p className="text-muted-foreground leading-relaxed">
              You agree to indemnify and hold harmless CannaBias, its officers, directors, employees, and agents from any claims, damages, or expenses arising from:
            </p>
            <ul className="space-y-2 text-muted-foreground ml-6">
              <li>• Your use of the Platform</li>
              <li>• Your violation of these Terms</li>
              <li>• Your violation of any third-party rights</li>
              <li>• Content you post on the Platform</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Governing Law</h2>
            <p className="text-muted-foreground leading-relaxed">
              These Terms are governed by the laws of [Jurisdiction], without regard to conflict of law principles. Any disputes shall be resolved in the courts of [Jurisdiction].
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Changes to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update these Terms of Use from time to time. We will notify you of material changes by:
            </p>
            <ul className="space-y-2 text-muted-foreground ml-6">
              <li>• Posting the updated terms on this page</li>
              <li>• Updating the "Last Updated" date</li>
              <li>• Sending an email to your registered address for significant changes</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed">
              Your continued use of the Platform after changes constitutes acceptance of the updated terms.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl">Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have questions about these Terms of Use, please contact us:
            </p>
            <div className="p-6 rounded-2xl bg-muted/30 border border-border">
              <p className="text-muted-foreground mb-2"><strong>Email:</strong> legal@cannabias.co.uk</p>
              <p className="text-muted-foreground mb-2"><strong>Mail:</strong> CannaBias Legal Department</p>
              <p className="text-muted-foreground mb-2">United Kingdom</p>
            </div>
          </section>

          <div className="p-6 rounded-2xl bg-primary/10 border border-primary/20 flex items-start gap-4">
            <AlertCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg mb-2">Important Reminder</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                By using CannaBias, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use and our Privacy Policy. If you do not agree, please discontinue use of the Platform immediately.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
